<template>
  <div class="ticket-detail-container">
    <!-- Loading -->
    <div v-if="loading" class="text-center my-5">
      <b-spinner label="Chargement..."></b-spinner>
      <p class="mt-2">Chargement...</p>
    </div>

    <!-- Create Sub-Ticket (inline) -->
    <div v-else-if="ticket && showCreateSubTicketModal">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h1><i class="fas fa-plus"></i> Créer un sous-ticket <span class="text-muted">du ticket #{{ ticket.id }}</span></h1>
        <b-button variant="outline-secondary" @click="showCreateSubTicketModal = false">
          <i class="fas fa-arrow-left"></i> Retour
        </b-button>
      </div>
      <b-card>
        <b-alert show variant="info" class="mb-3">
          <i class="fas fa-level-up-alt"></i> Ce ticket sera un sous-ticket de <strong>#{{ ticket.id }} — {{ ticket.titre }}</strong>
        </b-alert>
        <CreateTicketForm
          ref="subTicketForm"
          :parentTicketId="ticket.id"
          :clientUid="ticket.client_uid"
          :initialData="{ projet: ticket.projet }"
        />
        <div class="mt-3 d-flex justify-content-end">
          <b-button variant="outline-secondary" class="mr-2" @click="showCreateSubTicketModal = false">Annuler</b-button>
          <b-button variant="primary" @click="createSubTicket" :disabled="submitting">
            <b-spinner v-if="submitting" small class="mr-1"></b-spinner>
            Créer le sous-ticket
          </b-button>
        </div>
      </b-card>
    </div>

    <!-- Edit Ticket (inline) -->
    <div v-else-if="ticket && showEditModal">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h1><i class="fas fa-edit"></i> Modifier le ticket <span class="text-muted">#{{ ticket.id }}</span></h1>
        <b-button variant="outline-secondary" @click="showEditModal = false">
          <i class="fas fa-arrow-left"></i> Retour
        </b-button>
      </div>
      <b-card>
        <b-form @submit.prevent="updateTicket">
          <b-form-group label="Titre">
            <b-form-input v-model="editForm.titre" required></b-form-input>
          </b-form-group>

          <b-row>
            <b-col md="6">
              <b-form-group label="Type">
                <b-form-select v-model="editForm.type">
                  <b-form-select-option value="bug">Bug</b-form-select-option>
                  <b-form-select-option value="feature">Feature</b-form-select-option>
                  <b-form-select-option value="question">Question</b-form-select-option>
                  <b-form-select-option value="autre">Autre</b-form-select-option>
                </b-form-select>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Priorité">
                <b-form-select v-model="editForm.priorite">
                  <b-form-select-option value="basse">Basse</b-form-select-option>
                  <b-form-select-option value="moyenne">Moyenne</b-form-select-option>
                  <b-form-select-option value="haute">Haute</b-form-select-option>
                  <b-form-select-option value="urgente">Urgente</b-form-select-option>
                </b-form-select>
              </b-form-group>
            </b-col>
          </b-row>

          <b-form-group label="Statut">
            <b-form-select v-model="editForm.statut">
              <b-form-select-option value="ouvert">Ouvert</b-form-select-option>
              <b-form-select-option value="en cours">En cours</b-form-select-option>
              <b-form-select-option value="à tester">A tester</b-form-select-option>
              <b-form-select-option value="resolu">Résolu</b-form-select-option>
            </b-form-select>
          </b-form-group>

          <b-form-group label="Projet">
            <b-form-input
              v-model="editForm.projet"
              list="projects-list-edit"
              :disabled="!!ticket.parent_ticket"
              placeholder="Choisir un projet existant ou entrer un nouveau nom"
            ></b-form-input>
            <datalist id="projects-list-edit">
              <option v-for="project in projectsList" :key="project" :value="project">{{ project }}</option>
            </datalist>
            <b-form-text v-if="ticket.parent_ticket" class="text-muted">
              Le projet est hérité du ticket parent et ne peut pas être modifié ici.
            </b-form-text>
          </b-form-group>

          <b-form-group label="Assigné à">
            <b-form-select v-model="editForm.assigned_to">
              <b-form-select-option :value="null">Non assigné</b-form-select-option>
              <optgroup label="SmartMyData">
                <b-form-select-option v-for="user in smdUsersList" :key="user.uid" :value="user.uid">{{ user.text }}</b-form-select-option>
              </optgroup>
              <optgroup v-if="clientUsersList.length" label="Utilisateurs">
                <b-form-select-option v-for="user in clientUsersList" :key="user.uid" :value="user.uid">{{ user.text }}</b-form-select-option>
              </optgroup>
            </b-form-select>
          </b-form-group>

          <b-form-group label="Date de résolution">
            <b-form-input v-model="editForm.date_resolution" type="datetime-local"></b-form-input>
          </b-form-group>

          <b-form-group label="Demande">
            <vue-editor v-model="editForm.demande" :editorToolbar="messageToolbar" placeholder="Description de la demande..."></vue-editor>
          </b-form-group>

          <b-form-group v-if="isAdmin" label="Cause">
            <vue-editor v-model="editForm.cause" :editorToolbar="messageToolbar" placeholder="Cause du problème..."></vue-editor>
          </b-form-group>

          <b-form-group v-if="isAdmin" label="Correction">
            <vue-editor v-model="editForm.correction" :editorToolbar="messageToolbar" placeholder="Correction apportée..."></vue-editor>
          </b-form-group>

          <b-form-group v-if="isAdmin" label="À faire">
            <vue-editor v-model="editForm.a_faire" :editorToolbar="messageToolbar" placeholder="Actions à faire..."></vue-editor>
          </b-form-group>

          <div class="d-flex justify-content-end mt-3">
            <b-button variant="outline-secondary" class="mr-2" @click="showEditModal = false">Annuler</b-button>
            <b-button type="submit" variant="primary" :disabled="submitting">
              <b-spinner v-if="submitting" small class="mr-1"></b-spinner>
              Enregistrer
            </b-button>
          </div>
        </b-form>
      </b-card>
    </div>

    <!-- Ticket Detail -->
    <div v-else-if="ticket">
      <!-- Header -->
      <div class="d-flex justify-content-between align-items-start mb-4">
        <div>
          <b-button variant="link" @click="goBack" class="pl-0">
            <i class="fas fa-arrow-left"></i> Retour
          </b-button>
          <h1 class="mt-2">
            <span class="text-muted">#{{ ticket.id }}</span>
            {{ ticket.titre }}
          </h1>
        </div>
        <div class="d-flex align-items-center">
          <b-button
            :variant="isFollowing ? 'warning' : 'outline-secondary'"
            class="mr-2"
            @click="toggleFollow"
            v-b-tooltip.hover
            :title="isFollowing ? 'Se désabonner des notifications' : 'Suivre ce ticket'"
          >
            <i :class="isFollowing ? 'fas fa-bell-slash' : 'fas fa-bell'"></i>
            {{ isFollowing ? 'Ne plus suivre' : 'Suivre' }}
          </b-button>
          <div v-if="canEdit">
            <b-button variant="primary" @click="showCreateSubTicketModal = true" class="mr-2">
              <i class="fas fa-plus"></i> Créer un sous-ticket
            </b-button>
            <b-button variant="secondary" @click="showEditModal = true">
              <i class="fas fa-edit"></i> Modifier
            </b-button>
          </div>
        </div>
      </div>

      <!-- Ticket Info Card -->
      <b-card class="mb-4">
        <!-- Parent Ticket Alert -->
        <b-alert v-if="parentTicket" show variant="info" class="mb-3">
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <i class="fas fa-level-up-alt"></i>&nbsp;
              <strong>Ticket parent :</strong> 
              <a href="#" @click.prevent="viewTicket(parentTicket.id)" class="alert-link">
                #{{ parentTicket.id }} - {{ parentTicket.titre }}
              </a>
            </div>
            <b-badge :variant="getStatusVariant(parentTicket.statut)">
              {{ parentTicket.statut }}
            </b-badge>
          </div>
        </b-alert>

        <b-row>
          <b-col v-if="isAdmin" md="3" class="mb-3">
            <strong class="d-block mb-2">Client</strong>
            <b-badge variant="dark" class="p-2">
              <i class="fas fa-building"></i> {{ ticket.client_name || ticket.client_uid || '—' }}
            </b-badge>
          </b-col>

          <b-col md="3" class="mb-3">
            <strong class="d-block mb-2">Statut</strong>
            <div class="d-flex align-items-center">
              <b-badge :variant="getStatusVariant(ticket.statut)" class="p-2 mr-1">
                {{ ticket.statut }}
              </b-badge>
              <b-dropdown v-if="canEdit" size="sm" variant="outline-secondary" no-caret toggle-class="p-1" v-b-tooltip.hover title="Changer le statut">
                <template #button-content><i class="fas fa-chevron-down" style="font-size:10px"></i></template>
                <b-dropdown-item @click="quickUpdateStatut('ouvert')">Ouvert</b-dropdown-item>
                <b-dropdown-item @click="quickUpdateStatut('en cours')">En cours</b-dropdown-item>
                <b-dropdown-item @click="quickUpdateStatut('à tester')">À tester</b-dropdown-item>
                <b-dropdown-item @click="quickUpdateStatut('resolu')">Résolu</b-dropdown-item>
              </b-dropdown>
            </div>
          </b-col>

          <b-col md="3" class="mb-3">
            <strong class="d-block mb-2">Priorité</strong>
            <b-badge :variant="getPriorityVariant(ticket.priorite)" class="p-2">
              <i class="fas fa-exclamation-circle"></i> {{ ticket.priorite }}
            </b-badge>
          </b-col>

          <b-col md="3" class="mb-3">
            <strong class="d-block mb-2">Type</strong>
            <b-badge variant="secondary" class="p-2">
              <i class="fas fa-tag"></i> {{ ticket.type }}
            </b-badge>
          </b-col>

          <b-col md="3" class="mb-3">
            <strong class="d-block mb-2">Date de création</strong>
            <span>{{ formatDate(ticket.date) }}</span>
          </b-col>

          <b-col md="3" class="mb-3">
            <strong class="d-block mb-2">Créé par</strong>
            <span>
              <i class="fas fa-user-circle"></i> {{ ticket.user_display_name || ticket.client_user_uid || 'Inconnu' }}
            </span>
          </b-col>

          <b-col v-if="ticket.projet" md="3" class="mb-3">
            <strong class="d-block mb-2">Projet</strong>
            <span>
              <i class="fas fa-folder"></i> {{ ticket.projet }}
            </span>
          </b-col>

          <b-col v-if="ticket.assigned_to" md="3" class="mb-3">
            <strong class="d-block mb-2">Assigné à</strong>
            <span>
              <i class="fas fa-user"></i> {{ assignedUserName }}
            </span>
          </b-col>

          <b-col v-if="ticket.date_resolution" md="3" class="mb-3">
            <strong class="d-block mb-2">Date de résolution</strong>
            <span>
              <i class="fas fa-check-circle"></i> {{ formatDate(ticket.date_resolution) }}
            </span>
          </b-col>
        </b-row>

        <!-- Demande -->
        <div v-if="ticket.demande" class="mt-4">
          <h5><i class="fas fa-file-alt"></i> Demande</h5>
          <div v-html="ticket.demande" class="ticket-html-content"></div>
        </div>

        <!-- Cause -->
        <div v-if="ticket.cause" class="mt-4">
          <h5><i class="fas fa-search"></i> Cause</h5>
          <div v-html="ticket.cause" class="ticket-html-content"></div>
        </div>

        <!-- Correction -->
        <div v-if="ticket.correction" class="mt-4">
          <h5><i class="fas fa-wrench"></i> Correction</h5>
          <div v-html="ticket.correction" class="ticket-html-content"></div>
        </div>

        <!-- À faire -->
        <div v-if="ticket.a_faire" class="mt-4">
          <h5><i class="fas fa-tasks"></i> À faire</h5>
          <div v-html="ticket.a_faire" class="ticket-html-content"></div>
        </div>

        <!-- URLs -->
        <div v-if="ticket.urls" class="mt-4">
          <h5><i class="fas fa-link"></i> URLs</h5>
          <b-list-group>
            <b-list-group-item
              v-for="(url, index) in ticket.urls.split('\n')"
              :key="index"
              :href="url"
              target="_blank"
            >
              {{ url }}
            </b-list-group-item>
          </b-list-group>
        </div>

        <!-- Fichiers -->
        <div v-if="ticket.fichiers && ticket.fichiers.length" class="mt-4">
          <h5><i class="fas fa-paperclip"></i> Fichiers joints</h5>
          <b-row>
            <b-col v-for="(file, index) in ticket.fichiers" :key="index" md="3" class="mb-3">
              <b-card class="file-card h-100">
                <!-- Image thumbnail -->
                <div v-if="isImage(file)" class="file-thumbnail" @click="viewFile(file)">
                  <img :src="file.url" :alt="file.name" class="img-fluid" />
                </div>
                <!-- File icon -->
                <div v-else class="file-thumbnail file-icon" @click="viewFile(file)">
                  <i :class="getFileIcon(file)" class="fa-3x"></i>
                </div>
                <b-card-text class="mt-2">
                  <small class="text-truncate d-block">{{ file.name }}</small>
                  <small class="text-muted">{{ formatFileSize(file.size) }}</small>
                </b-card-text>
                <div class="d-flex justify-content-between">
                  <b-button size="sm" variant="primary" @click="downloadFile(file)">
                    <i class="fas fa-download"></i>
                  </b-button>
                  <b-button v-if="canEdit" size="sm" variant="danger" @click="deleteFile('ticket', ticket.id, file.key)">
                    <i class="fas fa-trash"></i>
                  </b-button>
                </div>
              </b-card>
            </b-col>
          </b-row>
        </div>

        <!-- Upload files to ticket -->
        <div v-if="canEdit" class="mt-4 ticket-files-section">
          <h5><i class="fas fa-upload"></i> Ajouter des fichiers</h5>
          <div 
            ref="ticketFilesPasteZone"
            class="paste-zone clickable"
            @click="addImageFromClipboard('ticketFiles')"
            tabindex="0"
          >
            <p class="text-muted text-center mb-2">
              <i class="fas fa-hand-pointer"></i> Cliquez ici après avoir copié une image pour l'ajouter
            </p>
          </div>
          <b-form-file
            v-model="ticketFiles"
            multiple
            placeholder="Choisir des fichiers..."
            drop-placeholder="Déposez les fichiers ici..."
            accept=".jpg,.jpeg,.png,.gif,.pdf,.csv,.txt,.doc,.docx"
          ></b-form-file>
          <small class="text-muted d-block mb-2">Max 10 Mo par fichier</small>
          
          <!-- Preview selected files -->
          <div v-if="ticketFiles && ticketFiles.length > 0" class="mt-3">
            <h6>Fichiers sélectionnés ({{ ticketFiles.length }}) :</h6>
            <b-row>
              <b-col v-for="(file, index) in ticketFiles" :key="index" md="3" class="mb-3">
                <b-card class="file-preview-card">
                  <div v-if="isImageFile(file)" class="file-preview-thumbnail">
                    <img :src="getFilePreviewUrl(file)" :alt="file.name" class="img-fluid" />
                  </div>
                  <div v-else class="file-preview-icon">
                    <i class="fas fa-file fa-3x text-secondary"></i>
                  </div>
                  <div class="file-preview-info mt-2">
                    <small class="d-block text-truncate" :title="file.name">{{ file.name }}</small>
                    <small class="text-muted">{{ formatFileSize(file.size) }}</small>
                  </div>
                  <b-button 
                    size="sm" 
                    variant="danger" 
                    class="mt-2 w-100"
                    @click="removeTicketFile(index)"
                  >
                    <i class="fas fa-trash"></i> Retirer
                  </b-button>
                </b-card>
              </b-col>
            </b-row>
          </div>

          <b-button 
            v-if="ticketFiles && ticketFiles.length > 0"
            variant="primary" 
            @click="uploadTicketFiles"
            :disabled="submitting"
            class="mt-2"
          >
            <i v-if="submitting" class="fas fa-spinner fa-spin"></i>
            <i v-else class="fas fa-upload"></i>
            {{ submitting ? 'Envoi...' : `Envoyer ${ticketFiles.length} fichier(s)` }}
          </b-button>
        </div>

        <!-- Sub-tickets List -->
        <div v-if="subTickets && subTickets.length > 0" class="mt-4">
          <h5><i class="fas fa-sitemap"></i> Sous-tickets ({{ subTickets.length }})</h5>
          <ul class="list-unstyled mb-0">
            <li v-for="sub in subTickets" :key="sub.id" class="d-flex justify-content-between align-items-center py-1">
              <a href="#" @click.prevent="viewTicket(sub.id)" class="alert-link">
                #{{ sub.id }} - {{ sub.titre }}
              </a>
              <b-badge :variant="getStatusVariant(sub.statut)">
                {{ sub.statut }}
              </b-badge>
            </li>
          </ul>
        </div>
      </b-card>

      <!-- Temps Section -->
      <b-card class="mb-4" v-if="isAdmin || tempsList.length > 0">
        <template #header>
          <div class="d-flex justify-content-between align-items-center">
            <h4 class="mb-0">
              <i class="fas fa-clock"></i> Temps passés
              <b-badge variant="secondary" class="ml-2">{{ tempsList.length }}</b-badge>
            </h4>
            <span class="text-muted font-weight-bold" v-if="tempsTotal">Total : {{ tempsTotal }}</span>
          </div>
        </template>

        <!-- Formulaire admin -->
        <div v-if="isAdmin" class="mb-4">
          <b-form @submit.prevent="addTemps">
            <b-row>
              <b-col md="2">
                <b-form-group label="Date *">
                  <b-form-input type="date" v-model="newTemps.date" required></b-form-input>
                </b-form-group>
              </b-col>
              <b-col md="2">
                <b-form-group label="Durée *">
                  <b-form-input type="time" v-model="newTemps.temps" step="60" min="00:01" max="12:00" required></b-form-input>
                </b-form-group>
              </b-col>
              <b-col md="2">
                <b-form-group label="Type">
                  <b-form-select v-model="newTemps.type_tache">
                    <b-form-select-option value="correctif">Correctif bug</b-form-select-option>
                    <b-form-select-option value="developpement">Développement</b-form-select-option>
                    <b-form-select-option value="documentation">Documentation</b-form-select-option>
                    <b-form-select-option value="formation">Formation</b-form-select-option>
                    <b-form-select-option value="gestion_projet">Gestion de projet</b-form-select-option>
                    <b-form-select-option value="maj">Mise à jour</b-form-select-option>
                    <b-form-select-option value="sav">SAV</b-form-select-option>
                    <b-form-select-option value="support">Support</b-form-select-option>
                    <b-form-select-option value="test">Test</b-form-select-option>
                  </b-form-select>
                </b-form-group>
              </b-col>
              <b-col md="1">
                <b-form-group label="Remise (%)">
                  <b-form-input type="number" v-model="newTemps.remise" min="0" max="100" placeholder="0"></b-form-input>
                </b-form-group>
              </b-col>
              <b-col md="4">
                <b-form-group label="Commentaire">
                  <b-form-textarea v-model="newTemps.commentaire" placeholder="Commentaire..." rows="2" max-rows="5"></b-form-textarea>
                </b-form-group>
              </b-col>
              <b-col md="1" class="d-flex align-items-end pb-3">
                <b-button type="submit" variant="primary" :disabled="submittingTemps" size="sm">
                  <i v-if="submittingTemps" class="fas fa-spinner fa-spin"></i>
                  <i v-else class="fas fa-plus"></i>
                </b-button>
              </b-col>
            </b-row>
          </b-form>
          <hr />
        </div>

        <!-- Liste des temps -->
        <div v-if="tempsLoading" class="text-center py-3">
          <b-spinner small label="Chargement..."></b-spinner>
        </div>
        <div v-else-if="tempsList.length === 0" class="text-muted text-center py-3">
          Aucun temps enregistré
        </div>
        <b-table v-else :items="tempsList" :fields="tempsFields" small striped hover responsive>
          <template #cell(date)="data">{{ formatDateShort(data.value) }}</template>
          <template #cell(commentaire)="data">
            <template v-if="!data.value">—</template>
            <template v-else-if="expandedCommentaires[data.item.id]">
              <span style="white-space: pre-wrap">{{ data.value }}</span>
              <b-button variant="link" size="sm" class="p-0 ml-1 align-baseline" @click="$set(expandedCommentaires, data.item.id, false)">−</b-button>
            </template>
            <template v-else>
              {{ truncateCommentaire(data.value) }}<span v-if="needsTruncation(data.value)" class="text-muted">…
                <b-button variant="link" size="sm" class="p-0 ml-1 align-baseline" @click="$set(expandedCommentaires, data.item.id, true)">+</b-button>
              </span>
            </template>
          </template>
          <template #cell(remise)="data">{{ data.value || 0 }}%</template>
          <template #cell(actions)="data">
            <b-button v-if="isAdmin" size="sm" variant="outline-secondary" class="mr-1" @click="editTemps(data.item)">
              <i class="fas fa-edit"></i>
            </b-button>
            <b-button v-if="isAdmin" size="sm" variant="outline-danger" @click="deleteTemps(data.item.id)">
              <i class="fas fa-trash"></i>
            </b-button>
          </template>
        </b-table>
      </b-card>

      <!-- Messages Section -->
      <b-card class="mb-4">
        <template #header>
          <h4 class="mb-0">
            <i class="fas fa-comments"></i> Messages
            <b-badge variant="secondary" class="ml-2">{{ messages.length }}</b-badge>
          </h4>
        </template>

        <!-- Messages List -->
        <div class="messages-list">
          <b-card
            v-for="message in messages"
            :key="message.id"
            class="mb-3"
            :class="{ 'border-primary': isOwnMessage(message) }"
          >
            <!-- Edit mode inline -->
            <div v-if="editingMessageId === message.id">
              <b-form @submit.prevent="updateMessage">
                <b-form-group label="Message">
                  <vue-editor
                    v-model="editMessageForm.message"
                    :editorToolbar="messageToolbar"
                    placeholder="Message..."
                  ></vue-editor>
                </b-form-group>

                <b-form-group label="Ajouter des fichiers (optionnel)">
                  <b-form-file
                    v-model="editMessageForm.newFiles"
                    multiple
                    placeholder="Choisir des fichiers..."
                    drop-placeholder="Déposez les fichiers ici..."
                    accept=".jpg,.jpeg,.png,.gif,.pdf,.csv,.txt,.doc,.docx"
                  ></b-form-file>
                  <small class="text-muted">Max 10 Mo par fichier</small>
                  <div v-if="editMessageForm.newFiles && editMessageForm.newFiles.length > 0" class="mt-2">
                    <b-row>
                      <b-col v-for="(file, index) in editMessageForm.newFiles" :key="'new-'+index" md="3" sm="6" class="mb-2">
                        <b-card class="file-preview-card-small">
                          <div v-if="isImageFile(file)" class="file-preview-thumbnail-small">
                            <img :src="getFilePreviewUrl(file)" :alt="file.name" class="img-fluid" />
                          </div>
                          <div v-else class="file-preview-icon-small">
                            <i class="fas fa-file fa-2x text-secondary"></i>
                          </div>
                          <div class="file-preview-info-small mt-1">
                            <small class="d-block text-truncate" :title="file.name">{{ file.name }}</small>
                          </div>
                          <b-button size="sm" variant="outline-danger" class="mt-1 w-100" @click="removeEditMessageNewFile(index)">
                            <i class="fas fa-times"></i>
                          </b-button>
                        </b-card>
                      </b-col>
                    </b-row>
                  </div>
                </b-form-group>

                <div class="d-flex justify-content-end">
                  <b-button variant="outline-secondary" class="mr-2" @click="editingMessageId = null">Annuler</b-button>
                  <b-button type="submit" variant="primary" :disabled="submitting">
                    <b-spinner v-if="submitting" small class="mr-1"></b-spinner>
                    Enregistrer
                  </b-button>
                </div>
              </b-form>
            </div>

            <!-- View mode -->
            <template v-else>
              <div class="d-flex justify-content-between align-items-start mb-2">
                <div>
                  <strong>
                    <i class="fas fa-user-circle"></i>
                    {{ message.user_display_name || message.client_user_uid }}
                  </strong>
                </div>
                <div class="text-muted">
                  <small>
                    {{ formatDate(message.date) }}
                    <span v-if="message.edit_date" class="ml-2">
                      (modifié {{ formatDate(message.edit_date) }})
                    </span>
                  </small>
                </div>
              </div>

              <div v-html="message.message" class="message-html-content mb-2"></div>

              <div v-if="message.fichiers && message.fichiers.length" class="mb-2">
                <b-row>
                  <b-col v-for="(file, index) in message.fichiers" :key="index" md="3" class="mb-2">
                    <div class="message-file-item">
                      <div v-if="isImage(file)" class="message-file-thumbnail" @click="viewFile(file)">
                        <img :src="file.url" :alt="file.name" class="img-fluid" />
                      </div>
                      <div v-else class="message-file-thumbnail file-icon" @click="viewFile(file)">
                        <i :class="getFileIcon(file)" class="fa-2x"></i>
                      </div>
                      <small class="text-truncate d-block mt-1">{{ file.name }}</small>
                      <div class="d-flex justify-content-between mt-1">
                        <b-button size="sm" variant="link" @click="downloadFile(file)">
                          <i class="fas fa-download"></i>
                        </b-button>
                        <b-button v-if="isOwnMessage(message)" size="sm" variant="link" class="text-danger" @click="deleteFile('message', message.id, file.key)">
                          <i class="fas fa-trash"></i>
                        </b-button>
                      </div>
                    </div>
                  </b-col>
                </b-row>
              </div>

              <div v-if="isOwnMessage(message)" class="text-right">
                <b-button size="sm" variant="outline-secondary" @click="editMessage(message)">
                  <i class="fas fa-edit"></i>
                </b-button>
              </div>
            </template>
          </b-card>
        </div>
      </b-card>

      <!-- New Message Form -->
      <b-card v-if="canEdit" class="new-message-section">
        <template #header>
          <h5 class="mb-0">Ajouter un message</h5>
        </template>
        <b-form @submit.prevent="sendMessage" id="new-message-form">
          <b-form-group label="Message *">
            <vue-editor
              v-model="newMessage.message"
              :editorToolbar="messageToolbar"
              placeholder="Écrivez votre message..."
            ></vue-editor>
          </b-form-group>

          <b-form-group label="Fichiers (optionnel)">
            <div 
              ref="newMessagePasteZone"
              class="paste-zone clickable"
              @click="addImageFromClipboard('newMessage')"
              tabindex="0"
            >
              <p class="text-muted text-center mb-2">
                <i class="fas fa-hand-pointer"></i> Cliquez ici après avoir copié une image pour l'ajouter
              </p>
            </div>
            <b-form-file
              v-model="newMessage.files"
              multiple
              placeholder="Choisir des fichiers..."
              drop-placeholder="Déposez les fichiers ici..."
              accept=".jpg,.jpeg,.png,.gif,.pdf,.csv,.txt,.doc,.docx"
            ></b-form-file>
            <small class="text-muted">Max 10 Mo par fichier</small>
            
            <!-- Preview selected files -->
            <div v-if="newMessage.files && newMessage.files.length > 0" class="mt-3">
              <strong>Fichiers sélectionnés ({{ newMessage.files.length }}) :</strong>
              <b-row class="mt-2">
                <b-col v-for="(file, index) in newMessage.files" :key="index" md="3" sm="6" class="mb-2">
                  <b-card class="file-preview-card-small">
                    <div v-if="isImageFile(file)" class="file-preview-thumbnail-small">
                      <img :src="getFilePreviewUrl(file)" :alt="file.name" class="img-fluid" />
                    </div>
                    <div v-else class="file-preview-icon-small">
                      <i class="fas fa-file fa-2x text-secondary"></i>
                    </div>
                    <div class="file-preview-info-small mt-1">
                      <small class="d-block text-truncate" :title="file.name">{{ file.name }}</small>
                      <small class="text-muted">{{ formatFileSize(file.size) }}</small>
                    </div>
                    <b-button 
                      size="sm" 
                      variant="outline-danger" 
                      class="mt-1 w-100"
                      @click="removeMessageFile(index)"
                    >
                      <i class="fas fa-times"></i>
                    </b-button>
                  </b-card>
                </b-col>
              </b-row>
            </div>
          </b-form-group>

          <div class="text-right">
            <b-button type="submit" variant="primary" :disabled="submitting">
              <i v-if="submitting" class="fas fa-spinner fa-spin"></i>
              <span v-else><i class="fas fa-paper-plane"></i> Envoyer</span>
            </b-button>
          </div>
        </b-form>
      </b-card>
    </div>

    <!-- File Viewer Modal -->
    <b-modal
      v-model="showFileModal"
      :title="currentFile ? currentFile.name : 'Fichier'"
      size="xl"
      ok-only
      ok-title="Fermer"
    >
      <div v-if="currentFile">
        <!-- Image viewer -->
        <div v-if="isImage(currentFile)" class="text-center">
          <img :src="currentFile.url" :alt="currentFile.name" class="img-fluid" style="max-height: 70vh;" />
        </div>
        <!-- CSV viewer -->
        <div v-else-if="isCSV(currentFile)">
          <viewFile :data="currentFile.url" :name="currentFile.name" />
        </div>
      </div>
    </b-modal>

    <!-- Create Sub-Ticket Modal -->
    <!-- (replaced by inline panel, see v-else-if below) -->

    <!-- Edit Temps Modal -->
    <b-modal
      v-model="showEditTempsModal"
      title="Modifier le temps"
      @ok="saveEditTemps"
      ok-title="Enregistrer"
      cancel-title="Annuler"
      :ok-disabled="submittingTemps"
    >
      <b-form v-if="editTempsForm">
        <b-row>
          <b-col md="6">
            <b-form-group label="Date *">
              <b-form-input type="date" v-model="editTempsForm.date" required></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Durée *">
              <b-form-input type="time" v-model="editTempsForm.temps" step="60" min="00:01" max="12:00" required></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>
        <b-form-group label="Type">
          <b-form-select v-model="editTempsForm.type_tache">
            <b-form-select-option value="correctif">Correctif bug</b-form-select-option>
            <b-form-select-option value="developpement">Développement</b-form-select-option>
            <b-form-select-option value="documentation">Documentation</b-form-select-option>
            <b-form-select-option value="formation">Formation</b-form-select-option>
            <b-form-select-option value="gestion_projet">Gestion de projet</b-form-select-option>
            <b-form-select-option value="maj">Mise à jour</b-form-select-option>
            <b-form-select-option value="sav">SAV</b-form-select-option>
            <b-form-select-option value="support">Support</b-form-select-option>
            <b-form-select-option value="test">Test</b-form-select-option>
          </b-form-select>
        </b-form-group>
        <b-form-group label="Remise (%)">
          <b-form-input type="number" v-model="editTempsForm.remise" min="0" max="100"></b-form-input>
        </b-form-group>
        <b-form-group label="Commentaire">
          <b-form-textarea v-model="editTempsForm.commentaire" rows="2" max-rows="5"></b-form-textarea>
        </b-form-group>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import ticketService from '../services/tickets';
import notifications_services from '../services/notifications.js';
import viewFile from '../components/viewFile.vue';
import CreateTicketForm from '../components/CreateTicketForm.vue';
import { VueEditor } from 'vue2-editor';

export default {
  name: 'TicketDetail',
  components: {
    viewFile,
    CreateTicketForm,
    VueEditor
  },
  data() {
    return {
      ticket: null,
      parentTicket: null,
      subTickets: [],
      messages: [],
      loading: false,
      submitting: false,
      showEditModal: false,
      showEditMessageModal: false,
      editingMessageId: null,
      showFileModal: false,
      showCreateSubTicketModal: false,
      currentFile: null,
      editForm: {},
      editMessageForm: {
        newFiles: []
      },
      newMessage: {
        message: '',
        files: []
      },
      messageToolbar: [
        [{ 'header': [false, 1, 2, 3, 4, 5, 6] }],
        [{ 'size': ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'align': '' }, { 'align': 'center' }, { 'align': 'right' }, { 'align': 'justify' }],
        ['blockquote', 'code-block'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'list': 'check' }],
        [{ 'color': [] }, { 'background': [] }],
        ['link', 'video'],
        ['clean']
      ],
      ticketFiles: [],
      usersList: [],
      projectsList: [],
      isFollowing: false,
      followersCount: 0,
      tempsList: [],
      tempsLoading: false,
      submittingTemps: false,
      showEditTempsModal: false,
      editTempsForm: null,
      expandedCommentaires: {},
      newTemps: {
        date: new Date().toISOString().slice(0, 10),
        temps: '',
        commentaire: '',
        remise: 0,
        type_tache: 'developpement'
      },
      tempsFields: [
        { key: 'date', label: 'Date', sortable: true },
        { key: 'collaborateur', label: 'Collaborateur' },
        { key: 'temps', label: 'Durée' },
        { key: 'type', label: 'Type' },
        { key: 'commentaire', label: 'Commentaire' },
        { key: 'remise', label: 'Remise' },
        { key: 'actions', label: '' }
      ]
    };
  },
  computed: {
    isAdmin() {
      const user = this.$store.state.user;
      return !!(user && user.details && user.details.client_uid);
    },
    smdUsersList() {
      return this.usersList.filter(u => u.uid === 'support_smd' || u.is_admin);
    },
    clientUsersList() {
      return this.usersList.filter(u => u.uid !== 'support_smd' && !u.is_admin);
    },
    canEdit() {
      const user = this.$store.state.user;
      if (!this.ticket || !user) return false;
      // Admins can always edit
      if (this.isAdmin) return true;
      // Normal users can edit their own tickets
      return true;
    },
    assignedUserName() {
      if (!this.ticket || !this.ticket.assigned_to) return null;
      const user = this.usersList.find(u => u.uid === this.ticket.assigned_to);
      return user ? user.text : this.ticket.assigned_to;
    },
    tempsTotal() {
      if (!this.tempsList.length) return null;
      let totalMinutes = 0;
      for (const t of this.tempsList) {
        const parts = (t.temps || '00:00').split(':');
        totalMinutes += parseInt(parts[0] || 0) * 60 + parseInt(parts[1] || 0);
      }
      const h = Math.floor(totalMinutes / 60);
      const m = totalMinutes % 60;
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    }
  },
  watch: {
    '$route.params.id'(newId, oldId) {
      if (newId !== oldId) {
        this.loadTicket();
        this.loadUsers();
        this.loadProjects();
        this.loadTemps();
      }
    },
    showEditMessageModal(newVal) {
      // kept for compatibility
    }
  },
  mounted() {
    this.loadTicket();
    this.loadUsers();
    this.loadProjects();
    this.loadTemps();

    // Dismiss la notification si elle existe (navigation directe ou via cloche)
    const ticketId = this.$route.params.id;
    const notifs = this.$store.getters.getTicketNotifs || [];
    const notif = notifs.find(n => String(n.ticket_id) === String(ticketId));
    if (notif) {
      // Un ticket peut avoir accumulé plusieurs notif_uid (un par événement) :
      // il faut tous les supprimer sinon les plus anciens réapparaissent au reload.
      const uids = new Set([...(notif.notif_uids || []), notif.notif_uid].filter(Boolean));
      uids.forEach(uid => notifications_services.delete(uid).catch(() => {}));
      this.$store.commit('removeTicketNotif', notif.ticket_id);
    }
    
    // Auto-focus the new message paste zone by default
    this.$nextTick(() => {
      if (this.$refs.newMessagePasteZone) {
        this.$refs.newMessagePasteZone.focus();
      }
    });
  },
  beforeDestroy() {
  },
  methods: {
    goBack() {
      // Récupérer l'URL précédente depuis l'historique du navigateur
      const previousRoute = window.history.state && window.history.state.back;
      
      // Vérifier si on vient de la page des tickets
      if (previousRoute && previousRoute.includes('/tickets')) {
        // Retour arrière pour conserver les filtres
        this.$router.back();
      } else {
        // Redirection vers la liste des tickets
        this.$router.push({ name: 'TicketsList' });
      }
    },

    async addImageFromClipboard(context) {
      try {
        const clipboardItems = await navigator.clipboard.read();
        const files = [];
        
        for (const clipboardItem of clipboardItems) {
          for (const type of clipboardItem.types) {
            if (type.startsWith('image/')) {
              const blob = await clipboardItem.getType(type);
              const fileName = `clipboard-image-${Date.now()}.png`;
              const file = new File([blob], fileName, { type: blob.type });
              files.push(file);
            }
          }
        }
        
        if (files.length === 0) {
          this.$notify({
            group: 'info',
            title: 'Aucune image',
            text: 'Aucune image trouvée dans le presse-papiers. Copiez une image d\'abord.',
            type: 'warning'
          });
          return;
        }
        
        // Add files to the appropriate context
        if (context === 'newMessage') {
          if (this.newMessage.files && this.newMessage.files.length > 0) {
            this.newMessage.files = [...this.newMessage.files, ...files];
          } else {
            this.newMessage.files = files;
          }
        } else if (context === 'editMessage') {
          if (this.editMessageForm.newFiles && this.editMessageForm.newFiles.length > 0) {
            this.editMessageForm.newFiles = [...this.editMessageForm.newFiles, ...files];
          } else {
            this.editMessageForm.newFiles = files;
          }
        } else if (context === 'ticketFiles') {
          if (this.ticketFiles && this.ticketFiles.length > 0) {
            this.ticketFiles = [...this.ticketFiles, ...files];
          } else {
            this.ticketFiles = files;
          }
        }
        
        this.$notify({
          group: 'info',
          title: 'Image ajoutée',
          text: `${files.length} image(s) ajoutée(s) depuis le presse-papiers`,
          type: 'success'
        });
      } catch (error) {
        console.error('Error reading clipboard:', error);
        this.$notify({
          group: 'info',
          title: 'Erreur',
          text: 'Impossible de lire le presse-papiers. Autorisez l\'accès au presse-papiers dans votre navigateur.',
          type: 'error'
        });
      }
    },

    handleTicketFilePaste(event) {
      const items = (event.clipboardData || event.originalEvent.clipboardData).items;
      const files = [];
      
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          const blob = items[i].getAsFile();
          const fileName = `pasted-image-${Date.now()}.png`;
          const file = new File([blob], fileName, { type: blob.type });
          files.push(file);
        }
      }
      
      if (files.length > 0) {
        // Add to existing files
        if (this.ticketFiles && this.ticketFiles.length > 0) {
          this.ticketFiles = [...this.ticketFiles, ...files];
        } else {
          this.ticketFiles = files;
        }
        
        this.$notify({
          group: 'info',
          title: 'Image ajoutée',
          text: `${files.length} image(s) ajoutée(s) depuis le presse-papiers`,
          type: 'success'
        });
        event.preventDefault();
      }
    },

    handleMessageFilePaste(event) {
      const items = (event.clipboardData || event.originalEvent.clipboardData).items;
      const files = [];
      
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          const blob = items[i].getAsFile();
          const fileName = `pasted-image-${Date.now()}.png`;
          const file = new File([blob], fileName, { type: blob.type });
          files.push(file);
        }
      }
      
      if (files.length > 0) {
        // Add to existing files
        if (this.newMessage.files && this.newMessage.files.length > 0) {
          this.newMessage.files = [...this.newMessage.files, ...files];
        } else {
          this.newMessage.files = files;
        }
        
        this.$notify({
          group: 'info',
          title: 'Image ajoutée',
          text: `${files.length} image(s) ajoutée(s) depuis le presse-papiers`,
          type: 'success'
        });
        event.preventDefault();
      }
    },

    handleEditMessageFilePaste(event) {
      const items = (event.clipboardData || event.originalEvent.clipboardData).items;
      const files = [];
      
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          const blob = items[i].getAsFile();
          const fileName = `pasted-image-${Date.now()}.png`;
          const file = new File([blob], fileName, { type: blob.type });
          files.push(file);
        }
      }
      
      if (files.length > 0) {
        // Add to existing files
        if (this.editMessageForm.newFiles && this.editMessageForm.newFiles.length > 0) {
          this.editMessageForm.newFiles = [...this.editMessageForm.newFiles, ...files];
        } else {
          this.editMessageForm.newFiles = files;
        }
        
        this.$notify({
          group: 'info',
          title: 'Image ajoutée',
          text: `${files.length} image(s) ajoutée(s) depuis le presse-papiers`,
          type: 'success'
        });
        event.preventDefault();
      }
    },

    isImageFile(file) {
      return file && file.type && file.type.startsWith('image/');
    },

    getFilePreviewUrl(file) {
      if (file && this.isImageFile(file)) {
        return URL.createObjectURL(file);
      }
      return '';
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    },

    removeTicketFile(index) {
      const files = Array.from(this.ticketFiles);
      files.splice(index, 1);
      this.ticketFiles = files.length > 0 ? files : [];
    },

    removeMessageFile(index) {
      const files = Array.from(this.newMessage.files);
      files.splice(index, 1);
      this.newMessage.files = files.length > 0 ? files : [];
    },

    removeEditMessageNewFile(index) {
      const files = Array.from(this.editMessageForm.newFiles);
      files.splice(index, 1);
      this.editMessageForm.newFiles = files.length > 0 ? files : [];
    },
    async loadUsers() {
      try {
        const data = await ticketService.listUsers();
        this.usersList = data.users || [];
      } catch (error) {
        console.error('Error loading users:', error);
        this.usersList = [];
      }
    },

    async loadProjects() {
      try {
        console.log('Loading projects...');
        const data = await ticketService.listProjects();
        console.log('Projects loaded:', data);
        this.projectsList = data.projects || [];
        console.log('projectsList:', this.projectsList);
      } catch (error) {
        console.error('Error loading projects:', error);
        this.projectsList = [];
      }
    },

    async loadTicket() {
      this.loading = true;
      try {
        const data = await ticketService.getTicket(this.$route.params.id);

        this.ticket = data.ticket;
        
        console.log('Ticket loaded:', this.ticket);
        console.log('Client name:', this.ticket.client_name);
        console.log('Is admin:', this.isAdmin);
        
        // Parse fichiers if they are strings
        if (this.ticket.fichiers && typeof this.ticket.fichiers === 'string') {
          try {
            this.ticket.fichiers = JSON.parse(this.ticket.fichiers);
          } catch (e) {
            console.error('Error parsing ticket fichiers:', e);
            this.ticket.fichiers = [];
          }
        }
        
        this.messages = data.messages;
        
        // Parse fichiers for each message
        if (this.messages && Array.isArray(this.messages)) {
          this.messages.forEach(msg => {
            if (msg.fichiers && typeof msg.fichiers === 'string') {
              try {
                msg.fichiers = JSON.parse(msg.fichiers);
              } catch (e) {
                console.error('Error parsing message fichiers:', e);
                msg.fichiers = [];
              }
            }
          });
        }

        // Load parent ticket if exists
        if (this.ticket.parent_ticket) {
          await this.loadParentTicket(this.ticket.parent_ticket);
        } else {
          this.parentTicket = null;
        }

        this.subTickets = data.subTickets || [];
        
        this.editForm = { ...this.ticket };
        
        // Load follow state
        this.loadFollowers();
        
        // Format date_resolution for datetime-local input (YYYY-MM-DDTHH:mm)
        if (this.editForm.date_resolution) {
          const date = new Date(this.editForm.date_resolution);
          if (!isNaN(date.getTime())) {
            // Format to YYYY-MM-DDTHH:mm
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            this.editForm.date_resolution = `${year}-${month}-${day}T${hours}:${minutes}`;
          }
        }
      } catch (error) {
        console.error('Error loading ticket:', error);
        this.$notify({
          group: 'info',
          title: 'Erreur',
          text: 'Erreur lors du chargement du ticket',
          type: 'error'
        });
      } finally {
        this.loading = false;
      }
    },

    async loadParentTicket(parentId) {
      try {
        const data = await ticketService.getTicket(parentId);
        this.parentTicket = data.ticket;
      } catch (error) {
        console.error('Error loading parent ticket:', error);
        this.parentTicket = null;
      }
    },

    async quickUpdateStatut(newStatut) {
      try {
        await ticketService.updateTicket(this.ticket.id, { statut: newStatut });
        this.ticket.statut = newStatut;
        this.$notify({ group: 'info', title: 'Statut mis à jour', text: newStatut, type: 'success' });
      } catch (error) {
        this.$notify({ group: 'info', title: 'Erreur', text: 'Erreur lors de la mise à jour du statut', type: 'error' });
        this.loadTicket();
      }
    },

    async updateTicket() {
      this.submitting = true;
      try {
        const updateData = { ...this.editForm };
        
        // Convert datetime-local format to MySQL datetime format
        if (updateData.date_resolution) {
          // datetime-local gives us YYYY-MM-DDTHH:mm, MySQL expects YYYY-MM-DD HH:mm:ss
          updateData.date_resolution = updateData.date_resolution.replace('T', ' ') + ':00';
        }
        
        // Le projet d'un sous-ticket est hérité du ticket parent, on ne l'envoie pas
        if (this.ticket.parent_ticket) {
          delete updateData.projet;
        }
        
        // Remove cause, correction and a_faire if user is not admin
        if (!this.isAdmin) {
          delete updateData.cause;
          delete updateData.correction;
          delete updateData.a_faire;
        }
        
        await ticketService.updateTicket(this.ticket.id, updateData);

        this.$notify({
          group: 'info',
          title: 'Succès',
          text: 'Ticket mis à jour',
          type: 'success'
        });
        this.showEditModal = false;
        this.loadTicket();
      } catch (error) {
        console.error('Error updating ticket:', error);
        this.$notify({
          group: 'info',
          title: 'Erreur',
          text: 'Erreur lors de la mise à jour',
          type: 'error'
        });
      } finally {
        this.submitting = false;
      }
    },

    async sendMessage() {
      this.submitting = true;
      try {
        const data = await ticketService.createMessage(
          this.$route.params.id,
          this.newMessage.message
        );

        const messageId = data.message_id;

        if (this.newMessage.files && this.newMessage.files.length > 0) {
          await ticketService.uploadMessageFiles(messageId, this.newMessage.files);
        }

        this.$notify({
          group: 'info',
          title: 'Succès',
          text: 'Message envoyé',
          type: 'success'
        });
        this.newMessage = { message: '', files: [] };
        this.loadTicket();
      } catch (error) {
        console.error('Error sending message:', error);
        this.$notify({
          group: 'info',
          title: 'Erreur',
          text: error.response?.data?.error || 'Erreur lors de l\'envoi du message',
          type: 'error'
        });
      } finally {
        this.submitting = false;
      }
    },

    editMessage(message) {
      this.editMessageForm = { ...message, newFiles: [] };
      this.editingMessageId = message.id;
    },

    async updateMessage() {
      this.submitting = true;
      try {
        await ticketService.updateMessage(this.editMessageForm.id, this.editMessageForm);

        // Upload new files if any
        if (this.editMessageForm.newFiles && this.editMessageForm.newFiles.length > 0) {
          await ticketService.uploadMessageFiles(this.editMessageForm.id, this.editMessageForm.newFiles);
        }

        this.$notify({
          group: 'info',
          title: 'Succès',
          text: 'Message mis à jour',
          type: 'success'
        });
        this.showEditMessageModal = false;
        this.editingMessageId = null;
        this.loadTicket();
      } catch (error) {
        console.error('Error updating message:', error);
        this.$notify({
          group: 'info',
          title: 'Erreur',
          text: 'Erreur lors de la mise à jour',
          type: 'error'
        });
      } finally {
        this.submitting = false;
      }
    },

    isOwnMessage(message) {
      if (this.isAdmin) return true;
      return message.client_user_uid === this.$store.state.user.uid;
    },

    getStatusVariant(statut) {
      const variants = {
        'ouvert': 'primary',
        'en cours': 'warning',
        'à tester': 'secondary',
        'resolu': 'success'
      };
      return variants[statut] || 'secondary';
    },

    getPriorityVariant(priorite) {
      const variants = {
        'urgente': 'danger',
        'haute': 'warning',
        'moyenne': 'info',
        'basse': 'success'
      };
      return variants[priorite] || 'secondary';
    },

    formatDate(date) {
      return new Date(date).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    async uploadTicketFiles() {
      if (!this.ticketFiles || this.ticketFiles.length === 0) return;

      try {
        await ticketService.uploadTicketFiles(this.ticket.id, this.ticketFiles);

        this.$notify({
          group: 'info',
          title: 'Succès',
          text: 'Fichiers ajoutés',
          type: 'success'
        });
        this.ticketFiles = [];
        this.loadTicket();
      } catch (error) {
        console.error('Error uploading files:', error);
        this.$notify({
          group: 'info',
          title: 'Erreur',
          text: error.response?.data?.error || 'Erreur lors de l\'upload',
          type: 'error'
        });
      }
    },

    async deleteFile(type, id, fileKey) {
      if (!confirm('Êtes-vous sûr de vouloir supprimer ce fichier ?')) return;

      try {
        if (type === 'ticket') {
          await ticketService.deleteTicketFile(id, fileKey);
        } else {
          await ticketService.deleteMessageFile(id, fileKey);
        }

        this.$notify({
          group: 'info',
          title: 'Succès',
          text: 'Fichier supprimé',
          type: 'success'
        });
        this.loadTicket();
      } catch (error) {
        console.error('Error deleting file:', error);
        this.$notify({
          group: 'info',
          title: 'Erreur',
          text: error.response?.data?.error || 'Erreur lors de la suppression',
          type: 'error'
        });
      }
    },

    viewFile(file) {
      if (this.isImage(file)) {
        // Open image in modal
        this.currentFile = file;
        this.showFileModal = true;
      } else if (this.isCSV(file)) {
        // Open CSV with viewFile component
        this.currentFile = file;
        this.showFileModal = true;
      } else {
        // Download other files
        this.downloadFile(file);
      }
    },

    downloadFile(file) {
      window.open(file.url, '_blank');
    },

    isImage(file) {
      const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
      const ext = (file.name || '').split('.').pop().toLowerCase();
      return imageExtensions.includes(ext) || (file.mimetype && file.mimetype.startsWith('image/'));
    },

    isCSV(file) {
      const ext = (file.name || '').split('.').pop().toLowerCase();
      return ext === 'csv' || file.mimetype === 'text/csv';
    },

    getFileIcon(file) {
      const ext = (file.name || '').split('.').pop().toLowerCase();
      const icons = {
        'pdf': 'fas fa-file-pdf text-danger',
        'doc': 'fas fa-file-word text-primary',
        'docx': 'fas fa-file-word text-primary',
        'xls': 'fas fa-file-excel text-success',
        'xlsx': 'fas fa-file-excel text-success',
        'csv': 'fas fa-file-csv text-success',
        'txt': 'fas fa-file-alt text-secondary',
        'zip': 'fas fa-file-archive text-warning',
        'rar': 'fas fa-file-archive text-warning'
      };
      return icons[ext] || 'fas fa-file text-secondary';
    },

    formatFileSize(bytes) {
      if (!bytes) return 'N/A';
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    },

    async createSubTicket() {
      this.submitting = true;
      try {
        const formData = this.$refs.subTicketForm.getData();
        const ticketData = {
          ...formData,
          client_uid: this.ticket.client_uid,
          projet: this.ticket.projet
        };
        
        const files = ticketData.files;
        delete ticketData.files;
        ticketData.parent_ticket = this.ticket.id;

        const data = await ticketService.createTicket(ticketData);
        const ticketId = data.ticket_id;

        if (files && files.length > 0) {
          await ticketService.uploadTicketFiles(ticketId, files);
        }

        this.$notify({
          group: 'info',
          title: 'Succès',
          text: 'Sous-ticket créé avec succès',
          type: 'success'
        });
        
        this.showCreateSubTicketModal = false;
        this.resetSubTicket();
        
        // Redirect to new ticket
        this.$router.push({ name: 'TicketDetail', params: { id: ticketId } });
      } catch (error) {
        console.error('Error creating sub-ticket:', error);
        this.$notify({
          group: 'info',
          title: 'Erreur',
          text: error.response?.data?.error || 'Erreur lors de la création du sous-ticket',
          type: 'error'
        });
      } finally {
        this.submitting = false;
      }
    },

    resetSubTicket() {
      if (this.$refs.subTicketForm) {
        this.$refs.subTicketForm.reset();
      }
    },

    viewTicket(ticketId) {
      this.$router.push({ name: 'TicketDetail', params: { id: ticketId } });
    },

    async loadFollowers() {
      try {
        const data = await ticketService.getFollowers(this.$route.params.id);
        this.isFollowing = data.is_following;
        this.followersCount = data.followers ? data.followers.length : 0;
      } catch (error) {
        console.error('Error loading followers:', error);
      }
    },

    editTemps(item) {
      this.editTempsForm = {
        id: item.id,
        date: item.date ? new Date(item.date).toISOString().slice(0, 10) : '',
        temps: item.temps || '',
        type_tache: item.type || 'developpement',
        remise: item.remise || 0,
        commentaire: item.commentaire || ''
      };
      this.showEditTempsModal = true;
    },

    async saveEditTemps() {
      this.submittingTemps = true;
      try {
        await ticketService.updateTemps(this.$route.params.id, this.editTempsForm.id, this.editTempsForm);
        this.$notify({ group: 'info', title: 'Succès', text: 'Temps mis à jour', type: 'success' });
        this.showEditTempsModal = false;
        this.editTempsForm = null;
        this.loadTemps();
      } catch (error) {
        this.$notify({ group: 'info', title: 'Erreur', text: error.response && error.response.data && error.response.data.error || 'Erreur', type: 'error' });
      } finally {
        this.submittingTemps = false;
      }
    },

    async loadTemps() {
      this.tempsLoading = true;
      try {
        const data = await ticketService.listTemps(this.$route.params.id);
        this.tempsList = data.temps || [];
      } catch (error) {
        if (error.response && error.response.status === 403) {
          this.tempsList = [];
        } else {
          console.error('Error loading temps:', error);
        }
      } finally {
        this.tempsLoading = false;
      }
    },

    async addTemps() {
      this.submittingTemps = true;
      try {
        await ticketService.addTemps(this.$route.params.id, this.newTemps);
        this.$notify({ group: 'info', title: 'Succès', text: 'Temps ajouté', type: 'success' });
        this.newTemps = {
          date: new Date().toISOString().slice(0, 10),
          temps: '',
          commentaire: '',
          remise: 0,
          type_tache: 'developpement'
        };
        this.loadTemps();
      } catch (error) {
        this.$notify({ group: 'info', title: 'Erreur', text: error.response && error.response.data && error.response.data.error || 'Erreur lors de l\'ajout', type: 'error' });
      } finally {
        this.submittingTemps = false;
      }
    },

    async deleteTemps(tacheId) {
      if (!confirm('Supprimer cette entrée de temps ?')) return;
      try {
        await ticketService.deleteTemps(this.$route.params.id, tacheId);
        this.$notify({ group: 'info', title: 'Succès', text: 'Temps supprimé', type: 'success' });
        this.loadTemps();
      } catch (error) {
        this.$notify({ group: 'info', title: 'Erreur', text: 'Erreur lors de la suppression', type: 'error' });
      }
    },

    truncateCommentaire(text) {
      const firstParagraph = (text || '').split(/\n/)[0];
      return firstParagraph.length > 50 ? firstParagraph.slice(0, 50) : firstParagraph;
    },
    needsTruncation(text) {
      if (!text) return false;
      const lines = text.split(/\n/);
      return lines.length > 1 || lines[0].length > 50;
    },
    formatDateShort(date) {
      return new Date(date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },

    async toggleFollow() {
      try {
        if (this.isFollowing) {
          await ticketService.unfollowTicket(this.$route.params.id);
          this.isFollowing = false;
          this.followersCount = Math.max(0, this.followersCount - 1);
          this.$notify({ group: 'info', title: 'Suivi désactivé', text: 'Vous ne suivez plus ce ticket', type: 'info' });
        } else {
          await ticketService.followTicket(this.$route.params.id);
          this.isFollowing = true;
          this.followersCount += 1;
          this.$notify({ group: 'info', title: 'Suivi activé', text: 'Vous suivez maintenant ce ticket', type: 'success' });
        }
      } catch (error) {
        console.error('Error toggling follow:', error);
        this.$notify({ group: 'info', title: 'Erreur', text: 'Erreur lors du changement de suivi', type: 'error' });
      }
    }
  }
};
</script>

<style scoped>
.paste-zone {
  border: 2px dashed #ccc;
  border-radius: 5px;
  padding: 15px;
  margin-bottom: 10px;
  background-color: #f8f9fa;
  cursor: pointer;
  transition: all 0.2s;
}

.paste-zone:hover,
.paste-zone:focus {
  border-color: #007bff;
  background-color: #e7f1ff;
  outline: none;
}

.paste-zone.clickable {
  cursor: pointer;
  transition: all 0.3s;
}

.paste-zone.clickable:hover {
  border-color: #007bff;
  background-color: #e7f1ff;
  transform: scale(1.02);
}

.paste-zone.clickable:active {
  transform: scale(0.98);
}

.paste-zone p {
  margin: 0;
  font-size: 14px;
}

.file-preview-card {
  height: 100%;
  text-align: center;
}

.file-preview-thumbnail {
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 5px;
  background-color: #f8f9fa;
}

.file-preview-thumbnail img {
  max-height: 150px;
  object-fit: contain;
}

.file-preview-icon {
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f9fa;
  border-radius: 5px;
}

.file-preview-info {
  min-height: 40px;
}

.file-preview-card-small {
  text-align: center;
  padding: 10px;
}

.file-preview-thumbnail-small {
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 5px;
  background-color: #f8f9fa;
}

.file-preview-thumbnail-small img {
  max-height: 80px;
  object-fit: contain;
}

.file-preview-icon-small {
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f9fa;
  border-radius: 5px;
}

.file-preview-info-small {
  min-height: 30px;
}

.ticket-detail-container {
  padding: 20px;
}

.messages-list {
  max-height: 600px;
  overflow-y: auto;
}

.ticket-info-card {
  background: white;
  border-radius: 8px;
  padding: 25px;
  margin-bottom: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 25px;
  padding-bottom: 25px;
  border-bottom: 2px solid #f0f0f0;
}

.info-item label {
  display: block;
  font-size: 12px;
  color: #666;
  text-transform: uppercase;
  margin-bottom: 5px;
  font-weight: 600;
}

.info-item span {
  display: block;
  font-size: 16px;
  color: #333;
}

.ticket-status {
  padding: 6px 14px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  display: inline-block;
}

.status-ouvert {
  background: #e3f2fd;
  color: #1976d2;
}

.status-en_cours {
  background: #fff3e0;
  color: #f57c00;
}

.status-resolu {
  background: #e8f5e9;
  color: #388e3c;
}

.status-ferme {
  background: #f5f5f5;
  color: #757575;
}

.ticket-priority {
  display: flex;
  align-items: center;
  gap: 5px;
  font-weight: 600;
}

.priority-urgente {
  color: #dc3545;
}

.priority-haute {
  color: #ff6b6b;
}

.priority-moyenne {
  color: #ffa500;
}

.priority-basse {
  color: #28a745;
}

.info-section {
  margin-top: 25px;
}

.info-section h3 {
  font-size: 16px;
  color: #333;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.text-content {
  color: #555;
  line-height: 1.6;
  white-space: pre-wrap;
}

.ticket-html-content,
.message-html-content {
  color: #555;
  line-height: 1.6;
}

.ticket-html-content >>> p,
.message-html-content >>> p {
  margin-bottom: 0rem;
}

.ticket-html-content >>> img,
.message-html-content >>> img {
  max-width: 100%;
  height: auto;
}

.ticket-html-content >>> a,
.message-html-content >>> a {
  color: #007bff;
  text-decoration: underline;
}

.files-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.file-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 4px;
  font-size: 14px;
}

.messages-section {
  background: white;
  border-radius: 8px;
  padding: 25px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.messages-header {
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid #f0f0f0;
}

.messages-header h2 {
  margin: 0;
  font-size: 22px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.message-count {
  color: #666;
  font-size: 18px;
}

.messages-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 30px;
}

.message-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 15px;
  border-left: 3px solid #ddd;
}

.message-card.own-message {
  background: #e3f2fd;
  border-left-color: #007bff;
}

.message-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.message-author {
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  gap: 8px;
}

.message-date {
  font-size: 12px;
  color: #666;
}

.edited-badge {
  font-style: italic;
}

.message-content {
  color: #333;
  line-height: 1.6;
  white-space: pre-wrap;
  margin-bottom: 10px;
}

.message-url {
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}

.message-url a {
  color: #007bff;
  word-break: break-all;
}

.message-files {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.file-tag {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 4px;
  font-size: 13px;
}

.message-actions {
  display: flex;
  gap: 8px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
}

.btn-icon {
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  font-size: 14px;
  padding: 4px 8px;
}

.btn-icon:hover {
  color: #007bff;
}

.new-message-form {
  padding-top: 20px;
  border-top: 2px solid #f0f0f0;
}

.new-message-form h3 {
  margin-bottom: 15px;
  font-size: 18px;
}

/* Modal and Form styles (reuse from TicketsList) */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #eee;
}

.modal-header h2 {
  margin: 0;
  font-size: 22px;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
}

.ticket-form {
  padding: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #333;
}

.form-control {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.form-control:focus {
  outline: none;
  border-color: #007bff;
}

.form-footer,
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 20px;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn-primary {
  background: #007bff;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #0056b3;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background: #5a6268;
}
</style>
